import { Dialog, Transition } from '@headlessui/react';

import { Fragment, useId, useState } from 'react';

import { isValidDecimalInput, normalizeDecimalInput } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type ExpenseLineItem,
  LINE_ITEM_LIMITS,
  formatAmountForCurrency,
  roundToCurrencyPrecision,
} from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import { useLineItemLabel } from './LineItemRow';

export interface LineItemFields {
  name: string;
  amount: number;
  quantity: number;
}

export interface LineItemEditorProps {
  isOpen: boolean;
  /** Null adds a new item. */
  item: ExpenseLineItem | null;
  currency: string;
  onSave: (fields: LineItemFields) => void;
  /** Only for an item that already exists. */
  onDelete?: () => void;
  onClose: () => void;
  /** After the leave transition, once the dialog has handed focus back. */
  onAfterLeave?: () => void;
}

const parseAmount = (value: string, currency: string): number | null => {
  if (value.trim() === '' || !isValidDecimalInput(value.trim())) return null;
  const parsed = normalizeDecimalInput(value.trim());
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return roundToCurrencyPrecision(parsed, currency);
};

const parseQuantity = (value: string): number | null => {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value.trim());
  return parsed >= LINE_ITEM_LIMITS.QUANTITY_MIN &&
    parsed <= LINE_ITEM_LIMITS.QUANTITY_MAX
    ? parsed
    : null;
};

const inputClass =
  'block w-full min-h-11 rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100';

function LineItemEditorContent({
  item,
  currency,
  onSave,
  onDelete,
  onClose,
}: Omit<LineItemEditorProps, 'isOpen' | 'onAfterLeave'>) {
  const { t } = useTranslation('expenses');
  const labelOf = useLineItemLabel();
  const [name, setName] = useState(item?.name ?? '');
  const [amount, setAmount] = useState(item ? String(item.amount) : '');
  const [quantity, setQuantity] = useState(String(item?.quantity ?? 1));
  const nameId = useId();
  const amountId = useId();
  const quantityId = useId();
  const unitPriceId = useId();

  const parsedAmount = parseAmount(amount, currency);
  const parsedQuantity = parseQuantity(quantity);
  const fields: LineItemFields | null =
    name.trim() !== '' && parsedAmount !== null && parsedQuantity !== null
      ? { name: name.trim(), amount: parsedAmount, quantity: parsedQuantity }
      : null;
  const unitPrice =
    parsedAmount !== null &&
    parsedAmount > 0 &&
    parsedQuantity !== null &&
    parsedQuantity > 1
      ? formatAmountForCurrency(
          roundToCurrencyPrecision(parsedAmount / parsedQuantity, currency),
          currency
        )
      : null;

  return (
    <>
      <Dialog.Title
        as="h3"
        className="text-lg font-semibold text-gray-900 dark:text-gray-100"
      >
        {item
          ? t('lineItems.editItem', { item: labelOf(item) })
          : t('lineItems.addItem')}
      </Dialog.Title>
      <form
        className="mt-4 space-y-4"
        onSubmit={event => {
          event.preventDefault();
          if (fields) onSave(fields);
        }}
      >
        <div>
          <label
            htmlFor={nameId}
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('lineItems.name')}
          </label>
          <input
            id={nameId}
            type="text"
            value={name}
            maxLength={LINE_ITEM_LIMITS.NAME_MAX}
            placeholder={t('lineItems.unnamed')}
            onChange={event => setName(event.target.value)}
            className={inputClass}
          />
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="min-w-[45%] flex-1">
            <label
              htmlFor={amountId}
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('lineItems.lineTotal')}
            </label>
            <input
              id={amountId}
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={event => setAmount(event.target.value)}
              className={inputClass}
            />
          </div>
          <div className="min-w-[30%] flex-1">
            <label
              htmlFor={quantityId}
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              {t('lineItems.quantity')}
            </label>
            <input
              id={quantityId}
              type="text"
              inputMode="numeric"
              value={quantity}
              onChange={event => setQuantity(event.target.value)}
              aria-describedby={unitPrice ? unitPriceId : undefined}
              className={inputClass}
            />
            {unitPrice ? (
              <p
                id={unitPriceId}
                data-testid="line-item-unit-price"
                className="mt-1 text-sm text-gray-500 dark:text-gray-400"
              >
                {t('lineItems.eachUnit', { amount: unitPrice })}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center">
          {onDelete ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onDelete}
              className="min-h-11 text-error-600 dark:text-error-400 sm:mr-auto"
            >
              {t('common:actions.delete')}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="min-h-11 sm:ml-auto"
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={fields === null}
            className="min-h-11"
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
    </>
  );
}

export function LineItemEditor({
  isOpen,
  onClose,
  onAfterLeave,
  ...props
}: LineItemEditorProps) {
  return (
    <Transition show={isOpen} as={Fragment} afterLeave={onAfterLeave}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150 motion-reduce:transition-none"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 dark:bg-black/60" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200 motion-reduce:transition-none"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-150 motion-reduce:transition-none"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800">
                <LineItemEditorContent onClose={onClose} {...props} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default LineItemEditor;
